/**
 * EventCheckIn — mobile-first door check-in for a donor event.
 *
 * Check-in previously lived as a cramped roster inside a `max-w-lg` modal, which
 * is the wrong surface for someone standing at a door with a phone. This is a
 * full-screen view: big search, big tap targets, a live attended count, and
 * walk-in registration inline.
 */

import React, { useMemo, useState } from 'react';
import { ArrowLeft, Check, Loader2, Search, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DonorEvent, DonorEventAttendee, checkInDonorEvent } from '@/services/api/donorEvents';

export const EventCheckIn: React.FC<{
  event: DonorEvent;
  onExit: () => void;
  onChanged: () => void;
}> = ({ event, onExit, onChanged }) => {
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [walkIn, setWalkIn] = useState(false);
  const [walkName, setWalkName] = useState('');
  const [walkEmail, setWalkEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attendees = event.attendees || [];
  const roster = useMemo(() => {
    const term = q.trim().toLowerCase();
    const rows = term
      ? attendees.filter(
          (a) => a.name.toLowerCase().includes(term) || (a.email || '').toLowerCase().includes(term),
        )
      : attendees;
    // Not-yet-checked-in first: the door only cares about who is still outside.
    return [...rows].sort((a, b) => Number(!!a.attended) - Number(!!b.attended) || a.name.localeCompare(b.name));
  }, [attendees, q]);

  const attendedCount = attendees.filter((a) => a.attended).length;

  const toggle = async (a: DonorEventAttendee) => {
    setBusyId(a.id);
    setError(null);
    try {
      await checkInDonorEvent(event.id, {
        member_id: a.member_id ?? undefined,
        external_email: a.external_email ?? undefined,
        external_name: a.name,
        attended: !a.attended,
      });
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Could not update check-in.');
    } finally {
      setBusyId(null);
    }
  };

  const addWalkIn = async () => {
    if (!walkName.trim() && !walkEmail.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await checkInDonorEvent(event.id, {
        external_email: walkEmail.trim() || `walkin-${Date.now()}@no-email.local`,
        external_name: walkName.trim() || 'Walk-in guest',
        attended: true,
      });
      setWalkName('');
      setWalkEmail('');
      setWalkIn(false);
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Could not add the walk-in.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-900">
      <header className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <button onClick={onExit} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
          <ArrowLeft size={20} />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">{event.title}</h1>
          <p className="text-xs text-gray-500">Door check-in</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-green-600">{attendedCount}</div>
          <div className="text-[11px] text-gray-500">of {attendees.length} here</div>
        </div>
      </header>

      <div className="border-b border-gray-200 p-3 dark:border-gray-700">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search guests…"
            className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-3 text-base dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
      </div>

      {error ? <div className="bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{error}</div> : null}

      <div className="flex-1 divide-y overflow-y-auto dark:divide-gray-800">
        {roster.length === 0 ? (
          <div className="px-4 py-16 text-center text-gray-400">
            {q ? 'No guests match that search.' : 'No guests on the list yet.'}
          </div>
        ) : (
          roster.map((a) => (
            <button
              key={a.id}
              onClick={() => toggle(a)}
              disabled={busyId === a.id}
              className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left active:bg-gray-50 disabled:opacity-60 dark:active:bg-gray-800"
            >
              <div className="min-w-0">
                <div className="truncate text-base font-medium text-gray-900 dark:text-gray-100">{a.name}</div>
                <div className="truncate text-xs text-gray-400">
                  {a.email || 'No email'} · {a.response_status}
                  {a.given ? ` · $${Math.round(a.given).toLocaleString('en-US')} given` : ''}
                </div>
              </div>
              <span
                className={`flex h-11 shrink-0 items-center gap-1.5 rounded-lg px-4 text-sm font-medium ${
                  a.attended
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                    : 'border border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                }`}
              >
                {busyId === a.id ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                {a.attended ? 'Here' : 'Check in'}
              </span>
            </button>
          ))
        )}
      </div>

      <div className="border-t border-gray-200 p-3 dark:border-gray-700">
        {walkIn ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input
                autoFocus
                value={walkName}
                onChange={(e) => setWalkName(e.target.value)}
                placeholder="Guest name"
                className="rounded border border-gray-300 px-3 py-2.5 text-base dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
              <input
                value={walkEmail}
                onChange={(e) => setWalkEmail(e.target.value)}
                placeholder="Email (for a receipt)"
                className="rounded border border-gray-300 px-3 py-2.5 text-base dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" disabled={saving} onClick={addWalkIn}>
                {saving ? <Loader2 size={15} className="mr-1 animate-spin" /> : <UserPlus size={15} className="mr-1" />}
                Add & check in
              </Button>
              <Button variant="outline" onClick={() => setWalkIn(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" className="w-full py-3" onClick={() => setWalkIn(true)}>
            <UserPlus size={16} className="mr-1.5" /> Add a walk-in guest
          </Button>
        )}
      </div>
    </div>
  );
};

export default EventCheckIn;
